import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../Authentication/LanguageContext";
import ServerUrl from "../../../../BACKEND";
import "./Nouns.css";

interface NounItem {
  id: number;
  headergerman: string;
  headertamil: string;
  headerenglish: string;
  headersinhala: string;
  headerhindi: string;
  headerarabic: string;
  headerturkish: string;
  headerrussian: string;
  headerchinese: string;
  headerfrench: string;
  headerspanish: string;
  subheadergerman: string;
  subheadertamil: string;
  subheaderenglish: string;
  subheadersinhala: string;
  subheaderhindi: string;
  subheaderarabic: string;
  subheaderturkish: string;
  subheaderrussian: string;
  subheaderchinese: string;
  subheaderfrench: string;
  subheaderspanish: string;
  contentgerman: string;
  contenttamil: string;
  contentenglish: string;
  contentsinhala: string;
  contenthindi: string;
  contentarabic: string;
  contentturkish: string;
  contentrussian: string;
  contentchinese: string;
  contentfrench: string;
  contentspanish: string;
  [key: string]: string | number;
}

const capitalize = (text: string) => {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};

const Nouns: React.FC = () => {
  const [nouns, setNouns] = useState<NounItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  const languageMap: Record<string, string> = {
    en: "english",
    de: "german",
    ta: "tamil",
    si: "sinhala",
    hi: "hindi",
    ar: "arabic",
    tr: "turkish",
    ru: "russian",
    zh: "chinese",
    fr: "french",
    es: "spanish",
  };

  const subtitleTranslations: Record<string, string> = {
    en: "Basics of Nouns",
    de: "Grundlagen der Nomen",
    ta: "பெயர்ச்சொற்களின் அடிப்படைகள்",
    si: "නාම පදයන්ගේ මූලික කරුණු",
    hi: "संज्ञाओं की मूल बातें",
    ar: "أساسيات الأسماء",
    tr: "İsimlerin Temelleri",
    ru: "Основы существительных",
    zh: "名词基础",
    fr: "Les bases des noms",
    es: "Fundamentos de los sustantivos",
  };

  useEffect(() => {
    fetch(`${ServerUrl}/api/nouns`)
      .then((res) => res.json())
      .then((json) => setNouns(json))
      .catch((error) => console.error("Error loading nouns:", error))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: NounItem, fieldPrefix: string) => {
    const fieldName = `${fieldPrefix}${languageMap[language] || "german"}`;
    const value = item[fieldName];
    return typeof value === "string" ? capitalize(value) : "";
  };

  const speakText = (text: string) => {
    if (currentlyPlaying === text) {
      setCurrentlyPlaying(null);
      speechSynthesis.cancel();
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    speechSynthesis.speak(utterance);
    setCurrentlyPlaying(text);

    utterance.onend = () => {
      setCurrentlyPlaying(null);
    };
  };

  return (
    <div className="nouns-page-container">
      <div className="nouns-page-header">
        <h1 className="nouns-page-title">Nomen</h1>
        <p className="nouns-page-subtitle">{subtitleTranslations[language]}</p>
      </div>

      {loading ? (
        <div className="nouns-page-loading">Loading...</div>
      ) : (
        <div className="nouns-page-scroll-container">
          {nouns.map((noun) => (
            <div key={noun.id} className="nouns-page-block-card">
              {/* Header */}
              <div className="nouns-page-block-header-container">
                <h2 className="nouns-page-block-header">
                  {noun.headergerman}
                  <span className="nouns-page-pipe-header"> | </span>
                  {getTranslation(noun, "header")}
                </h2>
              </div>

              {/* Subheader */}
              <div className="nouns-page-subheader-container">
                <p className="nouns-page-block-subheader">
                  {noun.subheadergerman}
                  <span className="nouns-page-pipe-subheader"> | </span>
                  {getTranslation(noun, "subheader")}
                </p>
              </div>

              {/* Content */}
              <div className="nouns-page-content-container">
                <div className="nouns-page-split-layout">
                  <p
                    className="nouns-page-content-german"
                    onClick={() => speakText(noun.contentgerman)}
                  >
                    {noun.contentgerman}
                  </p>
                  <div className="nouns-page-vertical-line"></div>
                  <p className="nouns-page-content-translated">
                    {getTranslation(noun, "content")}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Nouns;